/**
 * What admins deleted from the built-in Learning Zone on the Courses page. Its own categories and rows
 * are code (SIDEBAR_TREE), so deleting one hides it (backend services/hiddenCourseRows.js); the lists
 * come with GET .../learning-zone (students), .../courses/subcategories (admin Courses pages) and
 * .../syllabus-tracker/subcategories (coach pickers).
 */
import { GROUP_CATEGORIES } from './courseSubcategoryRows';

const lower = (value) => String(value || '').trim().toLowerCase();

const viewsIn = (rows, into = new Set()) => {
  rows.forEach((row) => {
    if (row.view) into.add(row.view);
    viewsIn(row.children || [], into);
  });
  return into;
};
const firstView = (rows) => {
  for (const row of rows) {
    if (row.view) return row.view;
    const below = firstView(row.children || []);
    if (below) return below;
  }
  return '';
};

/**
 * The Learning Zone tree without the groups of `hiddenCategories` (course category names) and the
 * built-in rows of `hiddenRows` ([{ category, key }], key = the row's id), each with the rows under it.
 * A row that opened a hidden row's screen (Computer Apps opens GCompris) opens its first remaining
 * row's instead, or, with none, just opens and closes. Rows of other groups stay as they are.
 */
export function withoutHiddenRows(tree, { hiddenRows = [], hiddenCategories = [] } = {}, groupCategories = GROUP_CATEGORIES) {
  if (!hiddenRows.length && !hiddenCategories.length) return tree;
  const gone = new Set(hiddenCategories.map(lower));
  const prune = (row, hidden) => {
    if (!row.children) return row;
    const kept = row.children.filter((child) => !hidden.has(child.id));
    const children = kept.map((child) => prune(child, hidden));
    if (kept.length === row.children.length && children.every((child, i) => child === row.children[i])) return row;
    const next = { ...row, children };
    const removed = viewsIn(row.children.filter((child) => hidden.has(child.id)));
    if (next.view && removed.has(next.view) && !viewsIn(children).has(next.view)) {
      const fallback = firstView(children);
      if (fallback) next.view = fallback;
      else delete next.view;
    }
    return next;
  };
  return tree
    .filter((group) => !gone.has(lower(groupCategories[group.id])))
    .map((group) => {
      const category = lower(groupCategories[group.id]);
      if (!category) return group;
      const hidden = new Set(hiddenRows.filter((row) => lower(row.category) === category).map((row) => row.key));
      return hidden.size ? prune(group, hidden) : group;
    });
}
